import axiosInstance from "./axiosInstance";
import { log, LogLevel } from "utils/LogUtil";
import {
  SubscriptionPlanMeta,
  SubscriptionPlanCode,
  SubscriptionInitDto,
  MySubscriptionDto,
  SubscriptionPayment,
} from "common/models/types";
import { ApiResponse } from "./pointsService";

export const fetchSubscriptionPlans = async (): Promise<
  ApiResponse<SubscriptionPlanMeta[]>
> => {
  try {
    const response = await axiosInstance.get<ApiResponse<SubscriptionPlanMeta[]>>(
      "/api/v1/subscriptions/plans"
    );
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "요금제 목록 조회 실패:", error);
    return { error: 1, message: "요금제 정보를 가져오지 못했습니다", data: [] };
  }
};

export const createSubscription = async (
  planCode: SubscriptionPlanCode,
  termsVersion: string
): Promise<ApiResponse<SubscriptionInitDto>> => {
  try {
    const response = await axiosInstance.post<ApiResponse<SubscriptionInitDto>>(
      "/api/v1/subscriptions",
      { planCode, termsVersion }
    );
    return response.data;
  } catch (error: any) {
    log(LogLevel.ERROR, "구독 신청 실패:", error);
    if (error.response?.data) return error.response.data;
    return { error: 1, message: "구독 신청에 실패했습니다" };
  }
};

export const fetchMySubscription = async (): Promise<
  ApiResponse<MySubscriptionDto>
> => {
  try {
    const response = await axiosInstance.get<ApiResponse<MySubscriptionDto>>(
      "/api/v1/subscriptions/me"
    );
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "구독 상태 조회 실패:", error);
    return { error: 1, message: "구독 정보를 가져오지 못했습니다" };
  }
};

export const fetchMySubscriptionPayments = async (params?: {
  limit?: number;
  offset?: number;
  startDate?: string;
  endDate?: string;
}): Promise<ApiResponse<SubscriptionPayment[]>> => {
  try {
    // undefined 값 제거 — URLSearchParams에 undefined가 들어가면 "undefined" 문자열로 직렬화됨
    const query = params
      ? `?${new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null)
            .reduce((acc, [k, v]) => ({ ...acc, [k]: String(v) }), {} as Record<string, string>)
        ).toString()}`
      : "";
    const response = await axiosInstance.get<ApiResponse<SubscriptionPayment[]>>(
      `/api/v1/subscriptions/me/payments${query}`
    );
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "구독 결제이력 조회 실패:", error);
    return { error: 1, message: "결제 이력을 가져오지 못했습니다", data: [] };
  }
};

/**
 * 결제 콜백 직후 백엔드에 결제 확정을 요청.
 *
 * ymyd-frontend의 USER_PREMIUM_PURCHASED_POST와 동일 패턴 — webhook/reconcile 의존성 제거.
 * 응답 지연 방지를 위해 5초 AbortController 타임아웃 적용 — 초과 시 reconcile 폴링으로 폴백.
 */
export const confirmSubscription = async (params: {
  impUid: string;
  merchantUid: string;
}): Promise<ApiResponse<MySubscriptionDto>> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 5000);
  try {
    const response = await axiosInstance.post<ApiResponse<MySubscriptionDto>>(
      "/api/v1/subscriptions/me/confirm",
      params,
      { signal: controller.signal }
    );
    return response.data;
  } catch (error: any) {
    log(LogLevel.ERROR, "구독 확정 실패:", error);
    if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
      return { error: 1, message: "결제 확정 응답 지연 — 백엔드 폴링 대기" };
    }
    if (error.response?.data) return error.response.data;
    return { error: 1, message: "결제 확정에 실패했습니다" };
  } finally {
    clearTimeout(timer);
  }
};

export const initBillingMethodChange = async (): Promise<
  ApiResponse<SubscriptionInitDto>
> => {
  try {
    const response = await axiosInstance.put<ApiResponse<SubscriptionInitDto>>(
      "/api/v1/subscriptions/me/billing-method"
    );
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "카드 변경 초기화 실패:", error);
    return { error: 1, message: "카드 변경을 시작할 수 없습니다" };
  }
};

export const cancelMySubscription = async (): Promise<
  ApiResponse<{ success: boolean }>
> => {
  try {
    const response = await axiosInstance.delete<ApiResponse<{ success: boolean }>>(
      "/api/v1/subscriptions/me"
    );
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "구독 해지 실패:", error);
    return { error: 1, message: "구독 해지에 실패했습니다" };
  }
};

export const resumeMySubscription = async (): Promise<
  ApiResponse<MySubscriptionDto>
> => {
  try {
    const response = await axiosInstance.post<ApiResponse<MySubscriptionDto>>(
      "/api/v1/subscriptions/me/resume"
    );
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "구독 재개 실패:", error);
    return { error: 1, message: "구독 재개에 실패했습니다" };
  }
};
